import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import MenuBar from './components/MenuBar.jsx';
import ModeSelection from './components/ModeSelection.jsx';
import HorizontalWorkflow from './components/HorizontalWorkflow.jsx';
import FileUpload from './components/FileUpload.jsx';
import DataPreview from './components/DataPreview.jsx';
import ProcessingPanel from './components/ProcessingPanel.jsx';
import RulesPanel from './components/RulesPanel.jsx';
import RulesModal from './components/RulesModal.jsx';
import EnrichmentPanel from './components/EnrichmentPanel.jsx';
import apiClient from './services/api';

const AppContainer = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  min-height: 100vh;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  padding: 20px;
`;

const RetroContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
  padding: 30px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
  
  h1 {
    color: #ffffff;
    text-align: center;
    font-size: 2.2rem;
    margin: 0;
    font-weight: 600;
    letter-spacing: 1px;
  }
  
  p {
    color: #e3f2fd;
    text-align: center;
    margin: 10px 0 0 0;
    font-size: 1rem;
    font-weight: 300;
  }
`;

const MainContent = styled.div`
  background: #ffffff;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  min-height: 400px;
`;

const ContentSection = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: #fafafa;
  border-radius: 12px;
  border: 2px solid #e0e0e0;
`;

const ResetButton = styled.button`
  background: #fff;
  color: #f44336;
  border: 2px solid #f44336;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Segoe UI', sans-serif;
  cursor: pointer;
  border-radius: 8px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f44336;
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
  }
`;

const QuickActionsBar = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  padding: 15px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  flex-wrap: wrap;
`;

const QuickButton = styled.button`
  background: ${props => props.$active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff'};
  color: ${props => props.$active ? '#fff' : '#667eea'};
  border: 1px solid ${props => props.$active ? '#667eea' : '#d0d0d0'};
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Segoe UI', sans-serif;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: ${props => props.$active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f3f8ff'};
    border-color: #667eea;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function App() {
  // Mode selection
  const [processingMode, setProcessingMode] = useState(null); // 'auto', 'rules', 'both'
  
  // Core state
  const [jobId, setJobId] = useState(null);
  const [files, setFiles] = useState([]);
  const [processingData, setProcessingData] = useState(null);
  const [activeView, setActiveView] = useState('upload');
  
  // Workflow states
  const [uploadStatus, setUploadStatus] = useState('pending');
  const [processingStatus, setProcessingStatus] = useState('pending');
  const [rulesStatus, setRulesStatus] = useState('pending');
  
  // UI states
  const [showPreview, setShowPreview] = useState(false);
  const [showRulesSection, setShowRulesSection] = useState(false);
  const [showEnrichment, setShowEnrichment] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  
  const processingTriggered = useRef(false);

  // Auto-trigger based on mode
  useEffect(() => {
    if (uploadStatus === 'completed' && !processingTriggered.current && jobId) {
      processingTriggered.current = true;
      
      // All modes trigger processing (Rules mode just loads data)
      setTimeout(() => startProcessing(), 1000);
    }
  }, [uploadStatus, jobId, processingMode]);

  // Show rules modal after processing in 'both' mode
  useEffect(() => {
    if (processingMode === 'both' && processingStatus === 'completed') {
      setTimeout(() => setShowRulesModal(true), 500);
    } else if (processingMode === 'auto' && processingStatus === 'completed') {
      toast.success('✅ Complete analysis done! Ready to download or enrich data.');
    } else if (processingMode === 'rules' && processingStatus === 'completed') {
      setRulesStatus('active');
      setShowRulesSection(true);
      toast.info('💡 Data loaded! Now define your filtering rules.');
    }
  }, [processingStatus, processingMode]);

  const handleModeSelect = (mode) => {
    console.log('📋 Mode selected:', mode);
    setProcessingMode(mode);
    setUploadStatus('active');
  };

  const handleFilesUploaded = (uploadedJobId, uploadedFiles) => {
    console.log('✅ Files uploaded:', uploadedJobId);
    setJobId(uploadedJobId);
    setFiles(uploadedFiles);
    setUploadStatus('completed');
    
    const modeNames = {
      'auto': 'AI Auto Processing',
      'rules': 'Rules AI',
      'both': 'AI Auto + Rules'
    };
    toast.success(`✅ Files uploaded! Starting ${modeNames[processingMode]}...`);
  };

  const startProcessing = async () => {
    if (!jobId) {
      toast.error('No job ID found!');
      return;
    }

    console.log(`🔄 Starting processing in ${processingMode} mode...`);
    setProcessingStatus('loading');

    try {
      const response = await apiClient.post(`/api/v1/process/${jobId}`, {
        mode: processingMode
      });
      
      setProcessingData(response.data);
      setProcessingStatus('completed');
      
      if (processingMode === 'rules') {
        toast.success('✅ Data loaded! Ready to apply rules.');
      } else {
        toast.success('✅ AI Processing completed!');
      }
    } catch (error) {
      console.error('❌ Processing error:', error);
      setProcessingStatus('active');
      toast.error('Processing failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRulesModalYes = () => {
    setShowRulesModal(false);
    setRulesStatus('active');
    setShowRulesSection(true);
    toast.info('💡 Define your filtering rules below');
  };

  const handleRulesModalNo = () => {
    setShowRulesModal(false);
    toast.success('✅ Ready to download your report!');
  };

  const handleDownload = async () => {
    if (!jobId) {
      toast.error('No job ID available');
      return;
    }

    toast.info('📥 Generating Excel file...');
    
    try {
      const response = await apiClient.get(`/api/v1/download/${jobId}`, {
        responseType: 'blob',
        params: { mode: processingMode }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const modePrefix = {
        'auto': 'AutoAI',
        'rules': 'Rules',
        'both': 'AutoAI_Rules'
      };
      
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.setAttribute('download', `SAP_Address_Report_${modePrefix[processingMode]}_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('✅ Excel report downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('❌ Download failed');
    }
  };

  const handleReset = () => {
    if (window.confirm('Start over with a new session?')) {
      setProcessingMode(null);
      setJobId(null);
      setFiles([]);
      setProcessingData(null);
      setUploadStatus('pending');
      setProcessingStatus('pending');
      setRulesStatus('pending');
      setShowPreview(false);
      setShowRulesSection(false);
      setShowEnrichment(false);
      setShowRulesModal(false);
      processingTriggered.current = false;
      toast.info('🔄 Session reset');
    }
  };

  return (
    <AppContainer>
      <RetroContainer>
        <Header>
          <h1>📍 SAP Address Data Quality Tool</h1>
          <p>AI-Powered Workflow for Address Validation & Master Data Cleansing</p>
        </Header>

        <MenuBar activeView={activeView} setActiveView={setActiveView} />

        <MainContent>
          {activeView === 'upload' && (
            <>
              {/* Step 1: Mode Selection */}
              {!processingMode && (
                <ModeSelection 
                  selectedMode={processingMode}
                  onModeSelect={handleModeSelect}
                />
              )}

              {/* Step 2: Upload & Processing */}
              {processingMode && (
                <>
                  <ResetButton onClick={handleReset}>
                    🔄 Change Mode / Start Over
                  </ResetButton>

                  <HorizontalWorkflow
                    mode={processingMode}
                    uploadStatus={uploadStatus}
                    processingStatus={processingStatus}
                    rulesStatus={rulesStatus}
                    onDownload={handleDownload}
                  />

                  {uploadStatus !== 'completed' && (
                    <ContentSection>
                      <FileUpload 
                        onFilesUploaded={handleFilesUploaded}
                        uploadedFiles={files}
                        onUploadStart={() => setUploadStatus('loading')}
                      />
                    </ContentSection>
                  )}

                  {processingStatus === 'loading' && (
                    <ContentSection>
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '20px' }}>
                          {processingMode === 'rules' ? '📁' : '⚙️'}
                        </div>
                        <h3 style={{ color: '#1976d2', marginBottom: '10px' }}>
                          {processingMode === 'rules' 
                            ? 'Loading Data...' 
                            : 'AI Processing in Progress...'}
                        </h3>
                        <p style={{ color: '#757575' }}>
                          {processingMode === 'rules'
                            ? 'Preparing your data for rule-based filtering'
                            : 'Analyzing data quality, detecting duplicates, and profiling columns'}
                        </p>
                        <div style={{ 
                          width: '100%', 
                          height: '4px', 
                          background: '#e0e0e0', 
                          borderRadius: '2px',
                          marginTop: '20px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, #4a90e2, #5c6bc0)',
                            animation: 'progress 2s ease-in-out infinite'
                          }}></div>
                        </div>
                        <style>{`
                          @keyframes progress {
                            0% { transform: translateX(-100%); }
                            100% { transform: translateX(100%); }
                          }
                        `}</style>
                      </div>
                    </ContentSection>
                  )}

                  {processingStatus === 'completed' && processingData && processingMode !== 'rules' && (
                    <ContentSection>
                      <h3 style={{ color: '#4caf50', marginBottom: '15px' }}>
                        ✅ Processing Completed Successfully!
                      </h3>
                      <ProcessingPanel status={processingData} />
                    </ContentSection>
                  )}

                  {jobId && files.length > 0 && (
                    <QuickActionsBar>
                      <QuickButton 
                        $active={showPreview}
                        onClick={() => setShowPreview(!showPreview)}
                      >
                        👁️ {showPreview ? 'Hide' : 'Show'} Data Preview
                      </QuickButton>
                      
                      {processingStatus === 'completed' && processingMode !== 'rules' && (
                        <QuickButton 
                          $active={showEnrichment}
                          onClick={() => setShowEnrichment(!showEnrichment)}
                        >
                          🌍 {showEnrichment ? 'Hide' : 'Show'} Postal Enrichment
                        </QuickButton>
                      )}
                      
                      {processingMode === 'both' && processingStatus === 'completed' && (
                        <QuickButton 
                          $active={showRulesSection}
                          onClick={() => setShowRulesSection(!showRulesSection)}
                        >
                          🎯 {showRulesSection ? 'Hide' : 'Show'} Apply Rules
                        </QuickButton>
                      )}
                    </QuickActionsBar>
                  )}

                  {showPreview && jobId && files.length > 0 && (
                    <ContentSection>
                      <DataPreview 
                        jobId={jobId} 
                        files={files}
                      />
                    </ContentSection>
                  )}

                  {showEnrichment && processingData && (
                    <ContentSection>
                      <EnrichmentPanel 
                        jobId={jobId}
                        onEnriched={(result) => {
                          console.log('✅ Enrichment completed:', result);
                          toast.success(`✅ Enriched ${result.enriched_count} postal codes!`);
                          // Optionally refresh data preview
                          if (showPreview) {
                            setShowPreview(false);
                            setTimeout(() => setShowPreview(true), 100);
                          }
                        }}
                      />
                    </ContentSection>
                  )}

                  {showRulesSection && (
                    <ContentSection>
                      <RulesPanel 
                        jobId={jobId}
                        processingStatus={processingData}
                        onRulesApplied={(result) => {
                          setRulesStatus('completed');
                          toast.success(`✅ Rules applied! ${result.filtered_count} rows match your filter.`);
                        }}
                      />
                    </ContentSection>
                  )}
                </>
              )}
            </>
          )}

          {activeView === 'connectors' && (
            <div style={{ textAlign: 'center', padding: '50px', color: '#757575' }}>
              <h2 style={{ color: '#667eea' }}>🔌 Data Source Connectors</h2>
              <p>Connect to SAP ERP, SQL databases, and cloud storage - Coming Soon</p>
            </div>
          )}

          {activeView === 'settings' && (
            <div style={{ textAlign: 'center', padding: '50px', color: '#757575' }}>
              <h2 style={{ color: '#667eea' }}>⚙️ Settings</h2>
              <p>Configure LLM provider, validation rules, and preferences - Coming Soon</p>
            </div>
          )}
        </MainContent>

        <ToastContainer 
          position="bottom-right"
          theme="light"
          autoClose={3000}
        />

        {/* Rules Confirmation Modal */}
        <RulesModal
          isOpen={showRulesModal}
          onClose={() => setShowRulesModal(false)}
          onYes={handleRulesModalYes}
          onNo={handleRulesModalNo}
        />
      </RetroContainer>
    </AppContainer>
  );
}

export default App;